import * as React from "react"
import { graphql, Link, PageProps } from "gatsby"

import {
  Layout,
  Seo,
  PostAuthor,
  Tag,
  ShareGrid,
  PostNavigation,
  AuthorBox,
  RelatedPosts,
} from "../components"
import { SameCategoryPosts } from "../components/post/same-category-posts"
import { CenteredLayout, Flex } from "../styles/base-styles"
import { WpPost } from "../types/wordpress-types"
import NotFoundPage from "./404"
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter"
import { coldarkDark } from "react-syntax-highlighter/dist/esm/styles/prism"
import {
  PostDiv,
  CategoriesContainer,
  PostTitle,
  PostContent,
  CategoryLabel,
} from "../styles/post-styles"
import { CATEGORY_SETTINGS, getCategoryColorWithId } from "../utils/constants"
import parse, { DOMNode, domToReact, Element } from "html-react-parser"

import { stripHtmlTags } from "../utils/sanitize"
import styled, { css } from "styled-components"
import { format } from "date-fns"
import { OrangeButton } from "../components/common/orange-button"
import { extractFilenameFromUrl, extractGifUrlFromUrl } from "../utils/urlUtils"
import { useCallback } from "react"
import { Socials } from "../components/common/socials"

interface PageData {
  wpPost: WpPost
}

interface PageContext {
  next: WpPost
  prev: WpPost
  categoryPostsTop4: WpPost[]
}

export const PostCode = ({
  language,
  children,
}: {
  language: string
  children: any
}) => (
  <PostCodeWrapper>
    <SyntaxHighlighter style={coldarkDark} language={language}>
      {children}
    </SyntaxHighlighter>
  </PostCodeWrapper>
)
const PostCodeWrapper = styled.div`
  margin: 20px 0 0 0;
`
const PostDateDiv = styled.div`
  color: #767676;
  font-feature-settings: "clig" off, "liga" off;
  font-family: san-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px; /* 133.333% */
  letter-spacing: 1.28px;
  /* SP style */
  @media screen and (max-width: 767px) {
    font-family: san-serif;
    font-size: 15px;
    font-style: normal;
    font-weight: 400;
    line-height: 180%; /* 27px */
    letter-spacing: 1.28px;
  }
`
const H2Container = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 64px 0 11.2px 0;
  max-width: 87.2vw;
  /* SP style */
  @media screen and (max-width: 767px) {
    padding: 32px 0 0;
  }
`
const H2Div = styled.h2`
  color: var(--Dark_Gray, #242424);
  font-feature-settings: "clig" off, "liga" off;
  /* H2 */
  font-family: san-serif;
  font-size: 34px;
  font-style: normal;
  font-weight: bold;
  line-height: 160%; /* 54.4px */
  letter-spacing: 1.28px;
  margin-right: 8px;
  word-break: break-word;
  /* SP style */
  @media screen and (max-width: 767px) {
    color: var(--Dark_Gray, #242424);
    font-feature-settings: "clig" off, "liga" off;
    /* SP_H2 */
    font-family: san-serif;
    font-size: 20px;
    font-style: normal;
    font-weight: 700;
    line-height: 160%; /* 32px */
    letter-spacing: 1.28px;
  }
`
const H2Border = styled.div`
  flex: 1 1 auto;
  min-width: 62px;
  height: 2px;
  background: var(--Dark_Gray, #242424);
  /* SP style */
  @media screen and (max-width: 767px) {
    min-width: 62px;
  }
`
const PostFigcaptionDiv = styled.div`
  margin: 15.5px;
  color: var(--Black, #000);
  text-align: center;
  font-feature-settings: "clig" off, "liga" off;
  font-family: san-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px; /* 150% */
  letter-spacing: 1.28px;
`

const PostHeader = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
  gap: 12px;
`
const BackToListDiv = styled.div`
  display: flex;
  justify-content: center;
  margin: 64px;
  /* SP style */
  @media screen and (max-width: 767px) {
    margin: 32px;
  }
`
const AuthorDiv = styled(Flex)`
  justify-content: space-between;
  align-items: flex-start;
  margin: 16px 0 16px;
  /* SP style */
  @media screen and (max-width: 767px) {
    margin: 8px 0 8px;
  }
`

const TagsAndSocials = styled.div`
  display: flex;
  justify-content: space-between;

  @media screen and (max-width: 767px) {
    flex-direction: column;
  }
`

const DatesContainer = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
`

const replaceCode = (node: DOMNode, post: WpPost) => {
  if (node.type === "tag") {
    const element = node as Element
    if (element.name === "pre") {
      const codeContent = getCode(element)
      return <PostCode language="javascript">{codeContent}</PostCode>
    }
    if (["h1", "h2"].includes(element.name)) {
      return (
        element.children.length > 0 && (
          <H2Container>
            <H2Div>{domToReact(element.children as any)}</H2Div>
            <H2Border />
          </H2Container>
        )
      )
    }
    if (element.name === "figcaption") {
      return (
        element.children.length > 0 && (
          <PostFigcaptionDiv>
            {domToReact(element.children as any)}
          </PostFigcaptionDiv>
        )
      )
    }

    if (element.name === "img") {
      const src = element.attribs.src || element.attribs["data-src"]
      const gifUrl = extractGifUrlFromUrl(src)

      if (gifUrl) {
        const newSrc = `/static/gifs/${
          post.databaseId
        }-${extractFilenameFromUrl(gifUrl)}`
        return <img src={newSrc} alt={element.attribs.alt || ""} />
      }
    }
  }
  return node
}

const getCode = (node: Element) => {
  let codeContent = ""
  node.children.forEach((child: any) => {
    if (child.data) {
      codeContent += child.data
    }
    if (child.children) {
      codeContent += getCode(child)
    }
  })
  return codeContent
}

const _getCode = (node: Element) => {
  return node.children.reduce((text, element: any) => {
    text += element.data ? element.data : ""
    if (element.children?.length > 0) {
      text += _getCode(element)
    }
    return text
  }, "")
}

export const Post = ({
  data: { wpPost: post },
  pageContext: { next, prev, categoryPostsTop4 },
  path,
}: PageProps<PageData, PageContext>) => {
  const getCategoryLink = useCallback((databaseId: number) => {
    return CATEGORY_SETTINGS.find(cs => cs.databaseId === databaseId)?.path
  }, [])

  if (!post) {
    return <NotFoundPage />
  }

  const {
    content,
    excerpt,
    title,
    categories,
    author,
    date,
    modified,
    tags,
    related_posts: relatedPosts,
    featuredImage,
  } = post

  const formattedDate = format(new Date(date), "yyyy年M月d日")
  const formattedModifiedDate = format(new Date(modified), "yyyy年M月d日")
  const isModifiedDateDifferent = formattedDate !== formattedModifiedDate
  const category = categories?.nodes[0]

  return (
    <Layout>
      <Seo
        title={title}
        description={stripHtmlTags(excerpt)}
        image={featuredImage?.node.sourceUrl || ""}
      />

      <div id="fb-root"></div>

      {/* Post Wrapper -----------------------------------------------------*/}
      <CenteredLayout size={880} mobilePadding={15}>
        {/* Post  ----------------------------------------------------------*/}
        <PostDiv>
          {/* Header with author and category ------------------------------*/}
          <PostHeader>
            {/* Category  --------------------------------------------------*/}
            <CategoriesContainer>
              {categories.nodes.map(category => (
                <Link
                  to={getCategoryLink(category.databaseId) || "/"}
                  key={category.id}
                >
                  <CategoryLabel strong color={"#b662d3"}>
                    {category.name}
                  </CategoryLabel>
                </Link>
              ))}
            </CategoriesContainer>

            {/* Dates  -----------------------------------------------------*/}
            <DatesContainer>
              <PostDateDiv>{formattedDate}</PostDateDiv>
              {isModifiedDateDifferent && (
                <PostDateDiv>{formattedModifiedDate}更新</PostDateDiv>
              )}
            </DatesContainer>
          </PostHeader>
          {/* Title  -------------------------------------------------------*/}
          <PostTitle>{title}</PostTitle>

          <AuthorDiv>
            {/* Author -----------------------------------------------------*/}
            <PostAuthor
              imageUrl={author?.node?.avatar?.url}
              name={author.node?.name}
            />
          </AuthorDiv>
          {/* Tags  --------------------------------------------------------*/}
          <TagsAndSocials>
            <div>
              {tags.nodes.length > 0 && (
                <Flex isWrap isStart>
                  {tags.nodes.map(tag => (
                    <Link to={"/tags"} key={tag.id} state={{ defaultTag: tag }}>
                      <Tag label={tag.name} />
                    </Link>
                  ))}
                </Flex>
              )}
            </div>

            <Socials />
          </TagsAndSocials>

          {/* Content  -----------------------------------------------------*/}
          <PostContent>
            {parse(content, { replace: node => replaceCode(node, post) })}
          </PostContent>

          {/* Author  ------------------------------------------------------*/}
          <AuthorBox
            name={author?.node?.name}
            position={author?.node?.lastName}
            description={author?.node?.description}
            url={author?.node?.avatar?.url}
          />

          {/* Share  -------------------------------------------------------*/}
          <ShareGrid />

          {/* Navigation between posts  --------------------------------------*/}
          <PostNavigation prev={prev} next={next} path={path} />

          {/* Back to list button */}
          <BackToListDiv>
            <OrangeButton href="/" label={"一覧へ戻る"} withIcon />
          </BackToListDiv>

          {/* Related posts  --------------------------------------*/}
          {relatedPosts.nodes.length ? (
            <RelatedPosts posts={relatedPosts.nodes} />
          ) : null}
          {/* Related posts  --------------------------------------*/}
          {categoryPostsTop4.length ? (
            <SameCategoryPosts
              posts={categoryPostsTop4}
              categoryDatabaseId={category?.databaseId}
            />
          ) : null}
        </PostDiv>
      </CenteredLayout>
    </Layout>
  )
}

export default Post

export const query = graphql`
  query MyQuery($id: Int = 0) {
    wpPost(databaseId: { eq: $id }) {
      id
      databaseId
      content
      slug
      excerpt
      featuredImage {
        node {
          sourceUrl
        }
      }
      tags {
        nodes {
          databaseId
          id
          name
        }
      }
      title
      date(formatString: "YYYY-MM-DD")
      modified(formatString: "YYYY-MM-DD")
      author {
        node {
          avatar {
            url
          }
          url
          name
          lastName
          description
        }
      }
      categories {
        nodes {
          databaseId
          id
          name
        }
      }
      related_posts {
        nodes {
          id
          databaseId
          title
          featuredImage {
            node {
              sourceUrl
              gatsbyImage(width: 400)
            }
          }
          date(formatString: "YYYY-MM-DD")
          categories {
            nodes {
              databaseId
              id
              name
            }
          }
        }
      }
    }
  }
`
